// Copyright (c) Microsoft. All rights reserved.

import { makeStyles } from '@fluentui/react-components';
import React from 'react';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { useAgentTypingIndicator } from '../../redux/features/livechat/useAgentTypingIndicator';
import { TypingIndicator } from './typing-indicator/TypingIndicator';

import { useTranslation } from 'react-i18next';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
    },
    slideIn: {
        animationName: {
            from: {
                opacity: 0,
                transform: 'translateY(2.4rem)',
            },
            to: {
                opacity: 1,
                transform: 'translateY(0)',
            },
        },
        animationDuration: '0.5s',
        animationTimingFunction: 'cubic-bezier(0.33, 0, 0, 1)',
        animationFillMode: 'forwards',
    },
});

interface ChatStatusProps {
    // When true, render only the agent-typing row and suppress the AI status line. Used as the
    // pinned slot in DynamicReport mode (#1933), where the AI statuses are owned by ChatStatusCard
    // inside the message flow — so this slot must not duplicate them.
    agentTypingOnly?: boolean;
}

export const ChatStatus: React.FC<ChatStatusProps> = ({ agentTypingOnly = false }) => {
    const classes = useClasses();
    const { t } = useTranslation();

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);

    // Agent-typing indicator (#1770), incl. the receiver-side safety auto-clear. This component is
    // the pinned status slot above the composer: in legacy mode (DynamicReport off) it carries both
    // the agent-typing row and the AI status line; in DynamicReport mode (#1933) it is rendered with
    // agentTypingOnly so only the agent-typing row pins here while ChatStatusCard owns the AI statuses.
    const isAgentTyping = useAgentTypingIndicator(selectedId);

    const botResponseStatus = conversations[selectedId].botResponseStatus;

    // The agent-typing indicator takes precedence over the AI status line. In practice they never
    // co-occur — once a human handles the chat the AI workflow is short-circuited server-side, so
    // no botResponseStatus arrives — but preferring the agent indicator keeps that guarantee local.
    const message: string | undefined | null = isAgentTyping
        ? t('liveChat.agentTyping')
        : agentTypingOnly
          ? undefined
          : botResponseStatus
            ? t('backendStatus.' + botResponseStatus)
            : botResponseStatus;

    if (!message) {
        return null;
    }

    return (
        <div className={classes.slideIn}>
            <div className={classes.root} aria-live="polite">
                <label>{message}</label>
                <TypingIndicator />
            </div>
        </div>
    );
};
